import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError } from "../api/client";
import {
  addOnsiteCartItem,
  formatMinorMoney,
  minorUnitsToDecimalString,
  onsiteItemCount,
  onsiteSubtotalMinor,
  parseMoneyToMinorUnits,
  setOnsiteCartItemQuantity,
  type ConfigResponse,
  type OnsiteBuyer,
  type OnsiteCartItem,
  type OnsiteSaleResult,
  type OnsiteVariant,
  type OnsiteVariantLookup,
} from "../shared/domain";
import { Dialog } from "./Dialog";
import { CloseablePanel } from "./Panel";
import { useKeyboardScanner } from "./useKeyboardScanner";

type OnsiteStep = "cart" | "payment" | "success";
type Theme = "light" | "dark";

interface OnsiteFeedback {
  kind: "success" | "error" | "warning";
  title: string;
  message: string;
  barcode?: string;
}

function newOperationKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return `onsite-${crypto.randomUUID()}`;
  return `onsite-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function displayName(variant: OnsiteVariant): string {
  const options = Object.entries(variant.options).filter(([name]) => !["color", "colour", "size"].includes(name.toLowerCase()));
  const color = Object.entries(variant.options).find(([name]) => ["color", "colour"].includes(name.toLowerCase()))?.[1];
  const size = Object.entries(variant.options).find(([name]) => name.toLowerCase() === "size")?.[1];
  return [color, size, ...options.map(([, value]) => value)].filter(Boolean).join(" / ") || "Variant details unavailable";
}

function hasBuyerInformation(buyer: OnsiteBuyer): boolean {
  return Object.values(buyer).some((value) => value.trim().length > 0);
}

function OnsiteImage({ variant, alt, className = "" }: { variant: OnsiteVariant; alt: string; className?: string }) {
  const sources = useMemo(() => [variant.imageUrl, variant.productImageUrl].filter((value): value is string => Boolean(value)), [variant.imageUrl, variant.productImageUrl]);
  const [sourceIndex, setSourceIndex] = useState(0);
  useEffect(() => setSourceIndex(0), [variant.variantId]);
  const source = sources[sourceIndex];
  return (
    <div className={`product-image ${className}`}>
      {source ? <img src={source} alt={alt} onError={() => setSourceIndex((index) => index + 1)} /> : <div className="image-placeholder" role="img" aria-label={`${alt}; image unavailable`}><span aria-hidden="true">✦</span><small>Image unavailable</small></div>}
    </div>
  );
}

function OnsiteHeader({ mode, theme, onToggleTheme, onBack, step }: { mode: ConfigResponse["mode"]; theme: Theme; onToggleTheme: () => void; onBack: () => void; step: OnsiteStep }) {
  return (
    <header className="app-header onsite-header">
      <div className="brand-row">
        <div>
          <p className="brand-mark">NATALIFY</p>
          <p className="brand-subtitle">on-site sale</p>
        </div>
        <div className="header-actions">
          <span className="mode-badge"><span className="status-dot" /> {mode === "mock" ? "Demo" : "Shopify"}</span>
          <button className="icon-button theme-toggle" type="button" onClick={onToggleTheme} aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}>{theme === "light" ? "☾" : "☀"}</button>
        </div>
      </div>
      <div className="onsite-header-row">
        <button className="button button-quiet onsite-back-button" type="button" onClick={onBack}>← Orders</button>
        <div className="onsite-stepper" aria-label="On-site sale steps">
          <span className={step === "cart" ? "onsite-step-current" : step === "payment" || step === "success" ? "onsite-step-done" : ""}>{step === "payment" || step === "success" ? "✓" : "1"} Scan</span>
          <span className="onsite-step-line" aria-hidden="true" />
          <span className={step === "payment" ? "onsite-step-current" : step === "success" ? "onsite-step-done" : ""}>{step === "success" ? "✓" : "2"} Confirm</span>
        </div>
      </div>
    </header>
  );
}

function Feedback({ feedback, onDismiss }: { feedback: OnsiteFeedback; onDismiss: () => void }) {
  return (
    <CloseablePanel className={`feedback feedback-${feedback.kind} onsite-feedback`} role={feedback.kind === "error" ? "alert" : "status"} resetKey={`${feedback.kind}|${feedback.title}|${feedback.message}|${feedback.barcode ?? ""}`} closeLabel={`Close ${feedback.title}`} onClose={onDismiss}>
      <span className="feedback-icon" aria-hidden="true">{feedback.kind === "success" ? "✓" : feedback.kind === "warning" ? "!" : "×"}</span>
      <div className="onsite-feedback-copy"><strong>{feedback.title}</strong><p>{feedback.message}</p>{feedback.barcode && <p className="feedback-detail">Barcode: <code>{feedback.barcode}</code></p>}</div>
    </CloseablePanel>
  );
}

function OnsiteCartItemRow({ item, currency, onQuantity, onRemove }: { item: OnsiteCartItem; currency: string; onQuantity: (quantity: number) => void; onRemove: () => void }) {
  const options = Object.entries(item.options).filter(([, value]) => value);
  const lineTotal = item.unitPrice.amountMinor * item.quantity;
  return (
    <article className="card onsite-cart-item">
      <OnsiteImage variant={item} alt={item.name} className="onsite-cart-image" />
      <div className="onsite-cart-item-copy">
        <div className="onsite-cart-item-heading"><h2 dir="auto">{item.name}</h2><button className="button button-quiet onsite-remove" type="button" onClick={onRemove}>Remove</button></div>
        <p className="onsite-variant-line">{options.length > 0 ? options.map(([name, value]) => <span key={name}>{name}: {value}</span>) : <span>Variant details unavailable</span>}</p>
        <p className="onsite-sku-line">SKU: <code>{item.sku || "—"}</code> · Barcode: <code>{item.barcode}</code></p>
        <div className="onsite-cart-item-bottom">
          <div className="onsite-quantity-controls" aria-label={`Quantity for ${item.name}`}>
            <button className="quantity-button" type="button" onClick={() => onQuantity(item.quantity - 1)} aria-label={`Decrease ${item.name} quantity`}>−</button>
            <span aria-live="polite">{item.quantity}</span>
            <button className="quantity-button" type="button" onClick={() => onQuantity(item.quantity + 1)} aria-label={`Increase ${item.name} quantity`}>+</button>
          </div>
          <div className="onsite-price-stack"><span>{formatMinorMoney(item.unitPrice.amountMinor, currency)} × {item.quantity}</span><strong>{formatMinorMoney(lineTotal, currency)}</strong></div>
        </div>
      </div>
    </article>
  );
}

function OnsiteSummary({ items, currency, onDone }: { items: OnsiteCartItem[]; currency: string | null; onDone: () => void }) {
  const itemCount = onsiteItemCount(items);
  const subtotal = onsiteSubtotalMinor(items);
  return (
    <aside className="card onsite-summary" aria-label="On-site cart summary">
      <div className="onsite-summary-row"><span>Items</span><strong>{itemCount}</strong></div>
      <div className="onsite-summary-row"><span>Shopify subtotal</span><strong>{currency ? formatMinorMoney(subtotal, currency) : "—"}</strong></div>
      <button className="button button-primary button-large button-full onsite-done-button" type="button" onClick={onDone} disabled={items.length === 0}>Done <span aria-hidden="true">→</span></button>
    </aside>
  );
}

function BuyerInformation({ buyer, expanded, onToggle, onChange }: { buyer: OnsiteBuyer; expanded: boolean; onToggle: () => void; onChange: (patch: Partial<OnsiteBuyer>) => void }) {
  const name = [buyer.firstName.trim(), buyer.lastName.trim()].filter(Boolean).join(" ") || "Walk-in Customer";
  return (
    <section className="card onsite-section-card">
      <div className="onsite-section-heading"><div><p className="eyebrow">Buyer</p><h2>{name}</h2><p className="muted">{hasBuyerInformation(buyer) ? "Optional buyer information" : "No customer information"}</p></div><button className="button button-secondary" type="button" onClick={onToggle}>{expanded ? "Hide fields" : "Add Buyer Information"}</button></div>
      {expanded && <div className="form-grid onsite-buyer-form">
        <div className="field"><label htmlFor="onsite-first-name">First name</label><input id="onsite-first-name" value={buyer.firstName} onChange={(event) => onChange({ firstName: event.target.value })} autoComplete="given-name" /></div>
        <div className="field"><label htmlFor="onsite-last-name">Last name</label><input id="onsite-last-name" value={buyer.lastName} onChange={(event) => onChange({ lastName: event.target.value })} autoComplete="family-name" /></div>
        <div className="field"><label htmlFor="onsite-phone">Phone</label><input id="onsite-phone" value={buyer.phone} onChange={(event) => onChange({ phone: event.target.value })} inputMode="tel" autoComplete="tel" /></div>
        <div className="field"><label htmlFor="onsite-email">Email</label><input id="onsite-email" value={buyer.email} onChange={(event) => onChange({ email: event.target.value })} type="email" inputMode="email" autoComplete="email" /></div>
      </div>}
    </section>
  );
}

function PaymentLine({ label, value, emphasis = false }: { label: string; value: ReactNode; emphasis?: boolean }) {
  return <div className={`onsite-payment-line ${emphasis ? "onsite-payment-line-emphasis" : ""}`}><span>{label}</span><strong>{value}</strong></div>;
}

function OnsitePaymentScreen({ items, currency, buyer, buyerExpanded, totalPaidInput, totalPaidMinor, totalPaidError, saleError, submitting, readOnly, onBuyerToggle, onBuyerChange, onTotalPaidChange, onBack, onComplete, onReconcile }: {
  items: OnsiteCartItem[];
  currency: string;
  buyer: OnsiteBuyer;
  buyerExpanded: boolean;
  totalPaidInput: string;
  totalPaidMinor: number | null;
  totalPaidError: string | null;
  saleError: ApiError | null;
  submitting: boolean;
  readOnly: boolean;
  onBuyerToggle: () => void;
  onBuyerChange: (patch: Partial<OnsiteBuyer>) => void;
  onTotalPaidChange: (value: string) => void;
  onBack: () => void;
  onComplete: () => void;
  onReconcile: () => void;
}) {
  const subtotal = onsiteSubtotalMinor(items);
  const itemCount = onsiteItemCount(items);
  const difference = totalPaidMinor === null ? null : totalPaidMinor - subtotal;
  const unknownOutcome = saleError?.payload.category === "unknown";
  return (
    <main className="screen-shell onsite-screen onsite-payment-screen" aria-label="On-site payment">
      <div className="onsite-screen-title"><div><p className="eyebrow">On-site sale</p><h1>{readOnly ? "Review only" : "Review and record"}</h1></div><span className="status-pill status-warning">{readOnly ? "Shopify read-only" : "Payment received externally"}</span></div>
      <div className="onsite-payment-content">
        {readOnly && <CloseablePanel className="notice notice-warning" role="status" resetKey="onsite-read-only" closeLabel="Close read-only notice"><span className="notice-icon" aria-hidden="true">!</span><div><strong>On-site sales are not enabled</strong><p>Grant Shopify write_orders, then enable the server release flag to complete sales.</p></div></CloseablePanel>}
        <BuyerInformation buyer={buyer} expanded={buyerExpanded} onToggle={onBuyerToggle} onChange={onBuyerChange} />
        <section className="card onsite-section-card onsite-price-section">
          <div className="onsite-section-heading"><div><p className="eyebrow">Payment / Price</p><h2>Confirm total paid</h2></div><span className="status-pill status-neutral">On-site payment</span></div>
          <div className="onsite-payment-lines">
            <PaymentLine label="Shopify subtotal" value={formatMinorMoney(subtotal, currency)} />
            <div className="onsite-total-editor"><div><label htmlFor="onsite-total-paid">Total paid</label><p className="tiny-note">Defaults to the Shopify subtotal. Use a lower amount for an on-site discount.</p></div><div className="onsite-money-input"><span aria-hidden="true">{currency}</span><input id="onsite-total-paid" value={totalPaidInput} onChange={(event) => onTotalPaidChange(event.target.value)} inputMode="decimal" aria-invalid={Boolean(totalPaidError)} aria-describedby={totalPaidError ? "onsite-total-error" : undefined} /></div></div>
            {totalPaidError && <p id="onsite-total-error" className="field-error onsite-total-error" role="alert">{totalPaidError}</p>}
            {difference !== null && difference !== 0 && <PaymentLine label={difference < 0 ? "On-site discount" : "Difference"} value={formatMinorMoney(difference, currency)} />}
          </div>
        </section>
        <section className="card onsite-section-card onsite-review-card">
          <div className="onsite-section-heading"><div><p className="eyebrow">Final Sale Summary</p><h2>{readOnly ? "Preview only" : "Ready to complete"}</h2></div><span className="status-pill status-neutral">{readOnly ? "No Shopify write" : "Shopify order"}</span></div>
          <div className="onsite-review-grid"><div><span className="label">Buyer</span><strong>{[buyer.firstName.trim(), buyer.lastName.trim()].filter(Boolean).join(" ") || "Walk-in Customer"}</strong></div><div><span className="label">Items</span><strong>{itemCount} item{itemCount === 1 ? "" : "s"}</strong></div><div><span className="label">Shopify subtotal</span><strong>{formatMinorMoney(subtotal, currency)}</strong></div><div><span className="label">Total paid</span><strong>{totalPaidMinor === null ? "—" : formatMinorMoney(totalPaidMinor, currency)}</strong></div><div><span className="label">Payment</span><strong>On-site payment</strong></div></div>
        </section>
        {saleError && <CloseablePanel className={`feedback ${unknownOutcome ? "feedback-warning" : "feedback-error"} onsite-sale-error`} role="alert" resetKey={`${saleError.payload.code}|${saleError.message}`} closeLabel="Close sale error"><span className="feedback-icon" aria-hidden="true">{unknownOutcome ? "!" : "×"}</span><div><strong>{unknownOutcome ? "Shopify confirmation needed." : "Could not record sale in Shopify."}</strong><p>{saleError.message}</p><p className="feedback-detail">{unknownOutcome ? "Do not create another sale. Check Shopify with the same operation first." : "Your cart and payment details are still here. Retry when ready."}</p></div></CloseablePanel>}
      </div>
      <div className="onsite-payment-actions"><button className="button button-secondary button-large" type="button" onClick={onBack} disabled={submitting}>← Back</button><button className="button button-primary button-large onsite-complete-button" type="button" onClick={unknownOutcome ? onReconcile : onComplete} disabled={readOnly || submitting || items.length === 0 || totalPaidMinor === null} title={readOnly ? "On-site sale creation is not enabled on the server" : undefined}>{submitting ? (unknownOutcome ? "Checking Shopify…" : "Recording in Shopify…") : readOnly ? "Sales disabled" : unknownOutcome ? "Check Shopify" : "Complete On-site Sale"}</button></div>
    </main>
  );
}

function OnsiteSuccessScreen({ sale, live, onNewSale, onBackToOrders }: { sale: OnsiteSaleResult; live: boolean; onNewSale: () => void; onBackToOrders: () => void }) {
  return (
    <main className="screen-shell onsite-screen onsite-success-screen" aria-label="On-site sale success">
      <section className="card onsite-success-card">
        <div className="onsite-success-icon" aria-hidden="true">✓</div>
        <p className="eyebrow">Sale recorded</p>
        <h1>Shopify Order {sale.orderNumber}</h1>
        <p className="muted">{live ? "Shopify confirmed the paid order and decremented inventory." : "Demo Shopify state and inventory were updated locally."}</p>
        <div className="onsite-success-total"><strong>{sale.itemCount} item{sale.itemCount === 1 ? "" : "s"}</strong><strong>{formatMinorMoney(sale.totalPaidMinor, sale.currency)}</strong></div>
        <div className="onsite-success-actions"><button className="button button-primary button-large" type="button" onClick={onNewSale}>New On-site Sale</button><button className="button button-secondary" type="button" onClick={onBackToOrders}>Back to Orders</button></div>
      </section>
    </main>
  );
}

export function OnsiteFlow({ config, theme, onToggleTheme, onBackToOrders }: { config: ConfigResponse; theme: Theme; onToggleTheme: () => void; onBackToOrders: () => void }) {
  const [step, setStep] = useState<OnsiteStep>("cart");
  const [items, setItems] = useState<OnsiteCartItem[]>([]);
  const [currency, setCurrency] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<OnsiteFeedback | null>(null);
  const [ambiguousBarcode, setAmbiguousBarcode] = useState<string | null>(null);
  const [ambiguousVariants, setAmbiguousVariants] = useState<OnsiteVariant[]>([]);
  const [scanPending, setScanPending] = useState(false);
  const [manualBarcode, setManualBarcode] = useState("");
  const [buyer, setBuyer] = useState<OnsiteBuyer>({ firstName: "", lastName: "", phone: "", email: "" });
  const [buyerExpanded, setBuyerExpanded] = useState(false);
  const [totalPaidInput, setTotalPaidInput] = useState("");
  const [totalPaidMinor, setTotalPaidMinor] = useState<number | null>(null);
  const [totalPaidError, setTotalPaidError] = useState<string | null>(null);
  const [saleError, setSaleError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [operationKey, setOperationKey] = useState(newOperationKey);
  const [success, setSuccess] = useState<OnsiteSaleResult | null>(null);
  const [discardSaleOpen, setDiscardSaleOpen] = useState(false);

  const activeCurrency = currency ?? items[0]?.unitPrice.currency ?? null;
  const subtotal = onsiteSubtotalMinor(items);

  useEffect(() => {
    const shouldWarn = items.length > 0 && step !== "success";
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarn) return;
      event.preventDefault();
      event.returnValue = "This on-site sale will be lost.";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [items.length, step]);

  const acceptVariant = (variant: OnsiteVariant, barcode: string) => {
    if (currency && currency !== variant.unitPrice.currency) {
      setFeedback({ kind: "error", title: "Currency changed in Shopify", message: "The scanned variant uses a different shop currency. Clear this sale and start again so the total stays consistent.", barcode });
      return;
    }
    const previous = items.find((item) => item.variantId === variant.variantId);
    const nextQuantity = (previous?.quantity ?? 0) + 1;
    setCurrency((current) => current ?? variant.unitPrice.currency);
    setItems((current) => addOnsiteCartItem(current, variant));
    setFeedback({ kind: "success", title: "Scan accepted", message: `${variant.name} · ${displayName(variant)} · quantity ${nextQuantity}.`, barcode });
    setAmbiguousBarcode(null);
    setAmbiguousVariants([]);
  };

  const lookupBarcode = async (rawBarcode: string) => {
    const barcode = rawBarcode.trim();
    if (!barcode || scanPending) return;
    setScanPending(true);
    setFeedback(null);
    try {
      const result: OnsiteVariantLookup = await api.lookupOnsiteVariant(barcode);
      if (result.status === "found") {
        acceptVariant(result.variant, barcode);
      } else if (result.status === "ambiguous") {
        setAmbiguousBarcode(result.barcode);
        setAmbiguousVariants(result.variants);
        setFeedback({ kind: "warning", title: "Barcode matches more than one variant", message: "Choose the exact Shopify variant before adding it to the sale.", barcode: result.barcode });
      } else {
        setFeedback({ kind: "error", title: "PRODUCT NOT FOUND", message: "No Shopify product matches this barcode. It was not added to the cart.", barcode: result.barcode });
      }
    } catch (error) {
      const apiError = error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "ONSITE_LOOKUP_ERROR", message: "Could not look up this barcode in Shopify. Retry the scan.", retryable: true }, 0);
      setFeedback({ kind: "error", title: "Shopify lookup failed", message: apiError.message, barcode });
    } finally {
      setScanPending(false);
    }
  };

  useKeyboardScanner({ enabled: step === "cart", blocked: scanPending || ambiguousVariants.length > 0, onScan: lookupBarcode });

  const goToPayment = () => {
    if (!items.length || !activeCurrency) return;
    const input = minorUnitsToDecimalString(subtotal, activeCurrency);
    setTotalPaidInput(input);
    setTotalPaidMinor(subtotal);
    setTotalPaidError(null);
    setSaleError(null);
    setStep("payment");
  };

  const changeTotalPaid = (value: string) => {
    setTotalPaidInput(value);
    if (!activeCurrency) {
      setTotalPaidError("Shopify currency is not available yet.");
      setTotalPaidMinor(null);
      return;
    }
    const parsed = parseMoneyToMinorUnits(value, activeCurrency);
    if (parsed === null) {
      setTotalPaidError(`Enter a non-negative amount with the correct ${activeCurrency} precision.`);
      setTotalPaidMinor(null);
    } else if (parsed > subtotal) {
      setTotalPaidError("A total above the Shopify subtotal is not supported without changing variant prices. Enter the subtotal or a lower paid amount.");
      setTotalPaidMinor(null);
    } else {
      setTotalPaidError(null);
      setTotalPaidMinor(parsed);
    }
  };

  const completeSale = async () => {
    if (submitting || !items.length || !activeCurrency || totalPaidMinor === null) return;
    setSubmitting(true);
    setSaleError(null);
    try {
      const response = await api.createOnsiteSale({ operationKey, currency: activeCurrency, expectedSubtotalMinor: subtotal, totalPaidMinor, buyer: hasBuyerInformation(buyer) ? buyer : null, items: items.map((item) => ({ variantId: item.variantId, quantity: item.quantity })) });
      setSuccess(response.sale);
      setItems([]);
      setStep("success");
    } catch (error) {
      setSaleError(error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "ONSITE_SALE_ERROR", message: "Could not record sale in Shopify. Retry without rescanning the products.", retryable: true }, 0));
    } finally {
      setSubmitting(false);
    }
  };

  const reconcileSale = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await api.reconcileOnsiteSale(operationKey);
      if (response.status === "success") {
        setSaleError(null);
        setSuccess(response.sale);
        setItems([]);
        setStep("success");
        return;
      }
      setSaleError(new ApiError({ category: "unknown", code: "ONSITE_SALE_STILL_UNRESOLVED", message: response.message, retryable: true, operationKey }, 409));
    } catch (error) {
      setSaleError(error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "ONSITE_RECONCILIATION_ERROR", message: "Could not check the on-site sale in Shopify. Check again before creating another sale.", retryable: true, operationKey }, 0));
    } finally {
      setSubmitting(false);
    }
  };

  const exit = () => {
    if (items.length > 0 && step !== "success") {
      setDiscardSaleOpen(true);
      return;
    }
    onBackToOrders();
  };

  const confirmExit = () => {
    setDiscardSaleOpen(false);
    onBackToOrders();
  };

  const startNewSale = () => {
    setItems([]);
    setCurrency(null);
    setFeedback(null);
    setAmbiguousBarcode(null);
    setAmbiguousVariants([]);
    setManualBarcode("");
    setBuyer({ firstName: "", lastName: "", phone: "", email: "" });
    setBuyerExpanded(false);
    setTotalPaidInput("");
    setTotalPaidMinor(null);
    setTotalPaidError(null);
    setSaleError(null);
    setSuccess(null);
    setOperationKey(newOperationKey());
    setStep("cart");
  };

  return (
    <div className="onsite-flow">
      <OnsiteHeader mode={config.mode} theme={theme} onToggleTheme={onToggleTheme} onBack={exit} step={step} />
      {step === "cart" ? (
        <main className="screen-shell onsite-screen" aria-label="On-site sale cart">
          <div className="onsite-screen-title"><div><p className="eyebrow">On-site sale</p><h1>Scan what was sold</h1></div><span className="onsite-ready-indicator"><span className="pulse-dot" /> Scanner ready</span></div>
          {feedback && <Feedback feedback={feedback} onDismiss={() => { setFeedback(null); setAmbiguousVariants([]); setAmbiguousBarcode(null); }} />}
          {ambiguousVariants.length > 0 && <CloseablePanel className="card onsite-ambiguous-card" role="region" resetKey={`${ambiguousBarcode ?? ""}|${ambiguousVariants.map((variant) => variant.variantId).join("|")}`} closeLabel="Close variant choices" onClose={() => { setAmbiguousVariants([]); setAmbiguousBarcode(null); }}><div><p className="eyebrow">Barcode needs a choice</p><h2>{ambiguousBarcode}</h2></div><div className="onsite-ambiguous-list">{ambiguousVariants.map((variant) => <button className="onsite-ambiguous-option" type="button" key={variant.variantId} onClick={() => acceptVariant(variant, ambiguousBarcode ?? variant.barcode)}><OnsiteImage variant={variant} alt="" className="onsite-ambiguous-image" /><span><strong>{variant.name}</strong><small>{displayName(variant)} · SKU {variant.sku || "—"}</small></span><span aria-hidden="true">→</span></button>)}</div></CloseablePanel>}
          <form className="onsite-manual-lookup" onSubmit={(event) => { event.preventDefault(); void lookupBarcode(manualBarcode); setManualBarcode(""); }}><label htmlFor="onsite-manual-barcode">Manual barcode lookup</label><div><input id="onsite-manual-barcode" value={manualBarcode} onChange={(event) => setManualBarcode(event.target.value)} placeholder="Use scanner or enter a barcode" inputMode="numeric" autoComplete="off" /><button className="button button-secondary" type="submit" disabled={scanPending || !manualBarcode.trim()}>Look up</button></div></form>
          <div className="onsite-cart-list" aria-live="polite">{items.length === 0 ? <section className="card onsite-empty-cart"><div className="onsite-scan-glyph" aria-hidden="true">⌁</div><h2>Ready for the first scan</h2><p>Scan a clothing barcode to look up the real Shopify variant.</p></section> : items.map((item) => <OnsiteCartItemRow key={item.variantId} item={item} currency={activeCurrency ?? item.unitPrice.currency} onQuantity={(quantity) => setItems((current) => setOnsiteCartItemQuantity(current, item.variantId, quantity))} onRemove={() => setItems((current) => setOnsiteCartItemQuantity(current, item.variantId, 0))} />)}</div>
          <OnsiteSummary items={items} currency={activeCurrency} onDone={goToPayment} />
        </main>
      ) : step === "payment" ? (
        <OnsitePaymentScreen items={items} currency={activeCurrency ?? "USD"} buyer={buyer} buyerExpanded={buyerExpanded} totalPaidInput={totalPaidInput} totalPaidMinor={totalPaidMinor} totalPaidError={totalPaidError} saleError={saleError} submitting={submitting} readOnly={!config.shopifyOnsiteSalesEnabled} onBuyerToggle={() => setBuyerExpanded((value) => !value)} onBuyerChange={(patch) => setBuyer((current) => ({ ...current, ...patch }))} onTotalPaidChange={changeTotalPaid} onBack={() => setStep("cart")} onComplete={() => { void completeSale(); }} onReconcile={() => { void reconcileSale(); }} />
      ) : success ? (
        <OnsiteSuccessScreen sale={success} live={config.mode === "shopify"} onNewSale={startNewSale} onBackToOrders={onBackToOrders} />
      ) : null}
      {discardSaleOpen && <Dialog title="Discard this sale?" description={`${onsiteItemCount(items)} scanned item${onsiteItemCount(items) === 1 ? "" : "s"} will be removed.`} onClose={() => setDiscardSaleOpen(false)}><div className="dialog-actions"><button className="button button-quiet" type="button" onClick={() => setDiscardSaleOpen(false)}>Keep sale</button><button className="button button-danger" type="button" onClick={confirmExit}>Discard sale</button></div></Dialog>}
    </div>
  );
}
